import { useState, useRef } from "react";
import { dismissModal } from "@/utils/modalClose";
import { createPortal } from "react-dom";
import {
  IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon,
  IonCard, IonInput, IonSelect, IonSelectOption, IonSpinner,
  IonSegment, IonSegmentButton, IonLabel,
} from "@ionic/react";
import { closeOutline, checkmarkOutline, addOutline, trashOutline, personOutline, briefcaseOutline } from "ionicons/icons";
import { FLOAT } from "@/components/SettingsRows";
import { IonDateInput } from "@/components/DateInput";
import { US_STATES } from "@/pages/app/AppPaystub";
import { CANADIAN_PROVINCES } from "@/utils/canadianTaxRates";
import { UK_PAY_FREQUENCIES } from "@/utils/ukPayPeriods";
import { userHeaders } from "@/utils/userAuth";
import { confirmAlert } from "@/utils/confirmAlert";
import { toast } from "@/utils/toast";
import StubImport from "@/components/StubImport";
import EmployeeExtras from "@/components/EmployeeExtras";
import { flagEmptyRequiredFields } from "@/utils/requiredFields";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const EMPTY = { country: "us", sin: "", province: "", postalCode: "", niNumber: "", postcode: "", workerType: "employee", taxSettings: {}, deductions: [], contributions: [], employerBenefits: [], absencePlans: [], name: "", employeeId: "", ssnLast4: "", email: "", address: "", city: "", state: "", zip: "", bankName: "", bankLast4: "", payType: "hourly", rate: "", annualSalary: "", payFrequency: "biweekly", payDay: "Friday", hireDate: "" };
const digits4 = (v) => String(v || "").replace(/\D/g, "").slice(0, 4);
// a SIN as 123 456 789, a National Insurance number in capitals
const sinFormat = (v) => String(v || "").replace(/\D/g, "").slice(0, 9).replace(/(\d{3})(?=\d)/g, "$1 ").trim();
const upper = (v) => String(v || "").toUpperCase();
// the country picks which stub the employee is paid on, and with it the
// identity number, the address fields, the pay frequencies and the currency
const COUNTRIES = [
  { value: "us", label: "United States", flag: "us" },
  { value: "ca", label: "Canada", flag: "ca" },
  { value: "uk", label: "United Kingdom", flag: "gb" },
];
const CURRENCY = { us: "$", ca: "CAD $", uk: "£" };
const FREQUENCIES = {
  us: [["weekly", "Weekly"], ["biweekly", "Bi-Weekly"]],
  ca: [["weekly", "Weekly"], ["biweekly", "Bi-Weekly"]],
  uk: UK_PAY_FREQUENCIES,
};
const countryOf = (f) => (f.country === "ca" || f.country === "uk" ? f.country : "us");

// Add or edit a business's employees — the same portal sheet as the other
// /app modals, with the pay stub form's Employee Information fields. Adding
// can take several at once: Add another employee stacks another form, and
// the checkmark saves them all. The employees belong to the business being
// operated as, never the account.
export default function EmployeeModal({ employee = null, businessName = "", onClose: onCloseNow, onSaved }) {
  const onClose = (e) => dismissModal(onCloseNow, e); // animated exit, then the real close

  const isMobile = window.innerWidth < 768;
  const editing = !!employee;
  const [forms, setForms] = useState(() => [{ ...EMPTY, ...(employee || {}) }]);
  const [busy, setBusy] = useState(false);
  const sheetRef = useRef(null); // the sheet, for the required-field check on save

  const patch = (fi, updates) => setForms((list) => list.map((f, i) => (i === fi ? { ...f, ...updates } : f)));
  const set = (fi, field, clean) => (e) => patch(fi, { [field]: clean ? clean(e.detail.value) : (e.detail.value ?? "") });
  const addAnother = () => setForms((list) => [...list, { ...EMPTY }]);
  const dropForm = (fi) => setForms((list) => list.filter((_, i) => i !== fi));

  const save = async () => {
    // every field is required: the empties turn red and the first scrolls into view
    const missing = flagEmptyRequiredFields(sheetRef.current);
    if (missing) { toast.error("Fill in the required fields first"); missing.scrollIntoView({ block: "center", behavior: "smooth" }); return; }
    if (forms.some((f) => !f.name.trim())) { toast.error(forms.length > 1 ? "Enter every employee's name" : "Enter the employee's name"); return; }
    setBusy(true);
    let saved = 0;
    try {
      let last = null;
      for (const f of forms) {
        const res = await fetch(`${BACKEND_URL}/api/user/employees${editing ? `/${employee.id}` : ""}`, {
          method: editing ? "PUT" : "POST",
          headers: userHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify(f),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok || d.success === false) throw new Error(d.detail || "Could not save");
        last = d.employee;
        saved += 1;
      }
      toast.success(editing ? "Employee updated" : saved > 1 ? `${saved} employees added` : "Employee added");
      onSaved?.(last);
      onClose();
    } catch (err) {
      toast.error(saved > 0 ? `${err.message}. The first ${saved} saved.` : err.message);
      if (saved > 0) {
        // the ones that made it are off the sheet; the list refreshes
        setForms((list) => list.slice(saved));
        onSaved?.(null);
      }
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!(await confirmAlert({ header: `Remove ${employee.name}?`, message: `${employee.name} is removed from ${businessName || "this business"}. Documents already made are kept.`, confirmText: "Remove" }))) return;
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/employees/${employee.id}`, { method: "DELETE", headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not remove");
      toast.success(`${employee.name} removed`);
      onSaved?.(null);
      onClose();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const s = {
    backdrop: { position: "fixed", inset: 0, zIndex: 10014, background: isMobile ? "var(--ion-background-color)" : "rgba(0,0,0,0.55)", display: "flex", alignItems: isMobile ? "stretch" : "center", justifyContent: "center" },
    sheet: { background: "var(--ion-background-color)", color: "var(--ion-text-color)", display: "flex", flexDirection: "column", width: "100%", maxWidth: isMobile ? "100%" : 600, height: isMobile ? "100%" : "auto", maxHeight: isMobile ? "100%" : "85vh", overflow: "hidden" },
  };
  return createPortal(
    <div className="modal-backdrop pm-modal employee-modal" style={s.backdrop}>
      <div ref={sheetRef} className="modal-slide-up" style={s.sheet}>
        <IonHeader>
          <IonToolbar style={{ "--background": "var(--ion-card-background)", "--color": "var(--ion-text-color)" }}>
            <IonButtons slot="start">
              <IonButton fill="clear" shape="round" onClick={onClose} aria-label="Close">
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1.8em", color: "var(--ion-text-color)" }}>
                  <IonIcon icon={closeOutline} style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />
                </span>
              </IonButton>
            </IonButtons>
            <IonTitle style={{ fontWeight: 700 }}>{editing ? "Edit Employee" : "Add Employee"}</IonTitle>
            <IonButtons slot="end">
              <IonButton fill="clear" shape="round" onClick={save} disabled={busy} title={editing ? "Save employee" : "Add employee"} aria-label={editing ? "Save employee" : "Add employee"} className="edit-sheet-save">
                <span slot="icon-only" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 0, flexShrink: 0, fontSize: "1rem", color: "var(--ion-color-success)" }}>
                  {busy ? <IonSpinner name="crescent" style={{ width: 22, height: 22 }} /> : <IonIcon icon={checkmarkOutline} className="bold-icon" style={{ fontSize: "inherit", color: "inherit", pointerEvents: "none" }} />}
                </span>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <div className="pm-body">
          {forms.map((form, fi) => (
            <IonCard key={fi} className="pm-card sheet-card">
              <div className="sheet-card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ flex: 1 }}>
                  {editing ? employee.name : fi === 0 ? `New employee${businessName ? ` at ${businessName}` : ""}` : `Employee #${fi + 1}`}
                </span>
                {/* camera: prefill this form from an existing stub */}
                <StubImport mode="employee" iconOnly onApply={(f) => patch(fi, f)} />
                {/* editing: the trash removes the employee (with the ask);
                    adding: it drops this form from the sheet */}
                <IonButton fill="clear" color="danger" size="small" style={{ margin: 0 }}
                  disabled={busy || (!editing && forms.length <= 1)}
                  onClick={() => (editing ? remove() : dropForm(fi))}
                  aria-label={editing ? "Remove employee" : "Remove this employee form"}>
                  <IonIcon slot="icon-only" icon={trashOutline} style={{ fontSize: 20 }} />
                </IonButton>
              </div>
              <div className="pm-form">
                {/* which country's stub this person is paid on */}
                <IonSegment mode="ios" value={countryOf(form)} onIonChange={(e) => {
                  const c = e.detail.value;
                  // a frequency the new country does not offer falls back to its usual one
                  const ok = FREQUENCIES[c].some(([v]) => v === form.payFrequency);
                  patch(fi, { country: c, ...(ok ? {} : { payFrequency: c === "uk" ? "monthly" : "biweekly" }) });
                }}>
                  {COUNTRIES.map((c) => (
                    <IonSegmentButton key={c.value} value={c.value} layout="icon-start">
                      <img className="country-flag" src={`/assets/images/${c.flag}_flag.svg`} alt="" />
                      <IonLabel>{c.label}</IonLabel>
                    </IonSegmentButton>
                  ))}
                </IonSegment>
                {/* employee or contractor, as the pay stub modal asks */}
                <IonSegment mode="ios" value={form.workerType || "employee"} onIonChange={(e) => patch(fi, { workerType: e.detail.value })}>
                  <IonSegmentButton value="employee" layout="icon-start"><IonIcon icon={personOutline} /><IonLabel>Employee (W-2)</IonLabel></IonSegmentButton>
                  <IonSegmentButton value="contractor" layout="icon-start"><IonIcon icon={briefcaseOutline} /><IonLabel>Contractor (1099)</IonLabel></IonSegmentButton>
                </IonSegment>
                <IonInput {...FLOAT} label="Employee Name *" value={form.name} onIonInput={set(fi, "name")} />
                <div style={{ display: "flex", gap: 10 }}>
                  <IonInput {...FLOAT} label="Employee ID *" placeholder="100012345" value={form.employeeId} onIonInput={set(fi, "employeeId")} style={{ flex: 1 }} />
                  {countryOf(form) === "ca" ? (
                    <IonInput {...FLOAT} label="SIN *" inputmode="numeric" maxlength={11} placeholder="123 456 789" value={form.sin} onIonInput={set(fi, "sin", sinFormat)} style={{ flex: 1 }} />
                  ) : countryOf(form) === "uk" ? (
                    <IonInput {...FLOAT} label="National Insurance Number *" maxlength={13} placeholder="QQ 12 34 56 C" value={form.niNumber} onIonInput={set(fi, "niNumber", upper)} style={{ flex: 1 }} />
                  ) : (
                    <IonInput {...FLOAT} label="Last 4 of SSN *" inputmode="numeric" maxlength={4} placeholder="1234" value={form.ssnLast4} onIonInput={set(fi, "ssnLast4", digits4)} style={{ flex: 1 }} />
                  )}
                </div>
                <IonDateInput label="Hire Date *" value={form.hireDate} onChange={(v) => patch(fi, { hireDate: v ?? "" })} />
                <IonInput {...FLOAT} label="Email *" type="email" inputmode="email" placeholder="sam@company.com" value={form.email} onIonInput={set(fi, "email")} />
              </div>
              <div className="admin-subbar sheet-card-head">Address</div>
              <div className="pm-form">
                <IonInput {...FLOAT} label="Address *" value={form.address} onIonInput={set(fi, "address")} />
                <IonInput {...FLOAT} label="City *" value={form.city} onIonInput={set(fi, "city")} />
                {countryOf(form) === "uk" ? (
                  <IonInput {...FLOAT} label="Postcode *" maxlength={8} placeholder="SW1A 1AA" value={form.postcode} onIonInput={set(fi, "postcode", upper)} />
                ) : countryOf(form) === "ca" ? (
                  <div style={{ display: "flex", gap: 10 }}>
                    <IonSelect {...FLOAT} label="Province *" interface="popover" value={form.province}
                      onIonChange={(e) => patch(fi, { province: e.detail.value ?? "" })} style={{ flex: 1 }}>
                      {CANADIAN_PROVINCES.map((pr) => <IonSelectOption key={pr.code} value={pr.code}>{pr.name}</IonSelectOption>)}
                    </IonSelect>
                    <IonInput {...FLOAT} label="Postal Code *" maxlength={7} placeholder="A1A 1A1" value={form.postalCode} onIonInput={set(fi, "postalCode", upper)} style={{ flex: 1 }} />
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 10 }}>
                    <IonSelect {...FLOAT} label="State *" interface="popover" value={form.state}
                      onIonChange={(e) => patch(fi, { state: e.detail.value ?? "" })} style={{ flex: 1 }}>
                      {US_STATES.map(([code, name]) => <IonSelectOption key={code} value={code}>{name}</IonSelectOption>)}
                    </IonSelect>
                    <IonInput {...FLOAT} label="Zip Code *" inputmode="numeric" placeholder="12345" value={form.zip} onIonInput={set(fi, "zip")} style={{ flex: 1 }} />
                  </div>
                )}
              </div>
              <div className="admin-subbar sheet-card-head">Pay</div>
              <div className="pm-form">
                <IonSegment mode="ios" value={form.payType || "hourly"} onIonChange={(e) => patch(fi, { payType: e.detail.value })}>
                  <IonSegmentButton value="hourly"><IonLabel>Hourly</IonLabel></IonSegmentButton>
                  <IonSegmentButton value="salary"><IonLabel>Salary</IonLabel></IonSegmentButton>
                </IonSegment>
                {(form.payType || "hourly") === "hourly"
                  ? <IonInput {...FLOAT} label={`Hourly Rate (${CURRENCY[countryOf(form)]}) *`} type="number" inputmode="decimal" value={form.rate} onIonInput={set(fi, "rate")} />
                  : <IonInput {...FLOAT} label={`Annual Salary (${CURRENCY[countryOf(form)]}) *`} type="number" inputmode="decimal" value={form.annualSalary} onIonInput={set(fi, "annualSalary")} />}
                <div style={{ display: "flex", gap: 10 }}>
                  <IonSelect {...FLOAT} label="Pay Frequency *" interface="popover" value={form.payFrequency}
                    onIonChange={(e) => patch(fi, { payFrequency: e.detail.value ?? "" })} style={{ flex: 1 }}>
                    {FREQUENCIES[countryOf(form)].map(([v, l]) => <IonSelectOption key={v} value={v}>{l}</IonSelectOption>)}
                  </IonSelect>
                  <IonSelect {...FLOAT} label="Pay Day *" interface="popover" value={form.payDay}
                    onIonChange={(e) => patch(fi, { payDay: e.detail.value ?? "" })} style={{ flex: 1 }}>
                    {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((d) => <IonSelectOption key={d} value={d}>{d}</IonSelectOption>)}
                  </IonSelect>
                </div>
              </div>
              <div className="admin-subbar sheet-card-head">Banking</div>
              <div className="pm-form">
                <IonInput {...FLOAT} label="Bank Name *" value={form.bankName} onIonInput={set(fi, "bankName")} />
                <IonInput {...FLOAT} label="Last 4 of Bank Account *" inputmode="numeric" maxlength={4} placeholder="5678" value={form.bankLast4} onIonInput={set(fi, "bankLast4", digits4)} />
                {editing && (
                  <IonButton expand="block" fill="outline" color="danger" disabled={busy} onClick={remove}>
                    Remove employee
                  </IonButton>
                )}
              </div>
              <EmployeeExtras form={form} country={countryOf(form)} patch={(u) => patch(fi, u)} />
            </IonCard>
          ))}
          {!editing && (
            <IonButton expand="block" fill="outline" disabled={busy} onClick={addAnother} className="add-another-employee" style={{ margin: 0 }}>
              <IonIcon slot="start" icon={addOutline} />
              Add another employee
            </IonButton>
          )}
        </div>
      </div>
    </div>,
    document.querySelector("ion-app") || document.body
  );
}
